import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { initializeApp, getApps, getApp } from "firebase/app";
import firebaseConfig from "../../firebase-applet-config.json";
import { auth } from "./auth";
import {
  ChatMessage,
  GeneratedImageAsset,
  WorkspaceItem,
  AppMode,
  ThemeMode,
  ChatThemeMode,
  AccuracyMode,
  ResponseLength,
  JobSensitivity,
  AiPreset,
  AiVerbosity,
} from "../types";

const app = getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);
export const db = getFirestore(app);

const EXPECTED_FIREBASE_PROJECT_ID = "gen-lang-client-0902287543";

const MAX_STORED_INLINE_PAYLOAD_CHARS = 30000;
const MAX_STORED_ATTACHMENT_TEXT_CHARS = 120000;
const MAX_STORED_MESSAGE_CHARS = 150000;
const MAX_STORED_REPORT_CHARS = 180000;
const MAX_ACTIVE_WORKSPACE_BYTES = 850000;
const MAX_BACKUP_WORKSPACE_BYTES = 950000;
const MAX_ARCHIVE_BYTES = 1100000;
const GENERATED_IMAGE_LOCAL_STORAGE_PREFIX = "rodgerspc:generated-image:";
const MAX_STORED_IMAGE_ASSET_DATA_URL_CHARS = 30000;

const WORKSPACE_ROUTING_VERSION = "db-visual-payload-storage-truth-v1";
const DEFAULT_MODEL_ROUTING_MODE = "rodgerspc_auto";

export enum OperationType {
  CREATE = "create",
  UPDATE = "update",
  DELETE = "delete",
  LIST = "list",
  GET = "get",
  WRITE = "write",
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  };
}

export interface SavedChatSummary {
  id: string;
  title: string;
  mode: AppMode | string;
  messageCount: number;
  estimatedBytes: number;
  createdAtIso: string;
  updatedAtIso: string;
  archivedAtIso?: string;
  pinned?: boolean;
}

export interface SavedChatRecord extends SavedChatSummary {
  userId: string;
  email: string;
  messages: ChatMessage[];
  items?: WorkspaceItem[];
  globalTheme?: ThemeMode;
  chatTheme?: ChatThemeMode | string;
  currentMode?: AppMode | string;
  driveSyncEnabled?: boolean;
  searchGroundingEnabled?: boolean;
  imageGenerationEnabled?: boolean;
  fastModeEnabled?: boolean;
  accuracyMode?: AccuracyMode | string;
  responseLength?: ResponseLength | string;
  jobSensitivity?: JobSensitivity | string;
  aiPreset?: AiPreset;
  modelRoutingMode?: string;
  modelRoutingVersion?: string;
}

export interface WorkspaceStatePayload {
  userId: string;
  email: string;

  globalTheme: ThemeMode;
  chatTheme: ChatThemeMode | string;
  currentMode: AppMode;

  driveSyncEnabled: boolean;
  searchGroundingEnabled: boolean;
  imageGenerationEnabled: boolean;
  fastModeEnabled: boolean;

  driveSidebarVisible: boolean;
  bottomPanelVisible: boolean;

  aiTemperature: number;
  aiBehavior: AccuracyMode;
  aiVerbosity?: AiVerbosity;

  accuracyMode?: AccuracyMode | string;
  responseLength?: ResponseLength | string;
  jobSensitivity?: JobSensitivity | string;
  aiPreset?: AiPreset;

  /**
   * Server-side model choice is automatic.
   * Do not persist per-prompt modelRoutingHint here; that belongs only in the live /api/chat request.
   */
  modelRoutingMode?: string;
  modelRoutingVersion?: string;

  messages: ChatMessage[];
  items: WorkspaceItem[];

  isCleared?: boolean;
  clearedAt?: string | null;
  activeChatId?: string;
  lastUpdated?: string;

  backedUpAtIso?: string;
  source?: string;
}

/**
 * Firestore path design:
 *
 * Active working chat:
 * users/{uid}/workspaceState/active
 *
 * Last cleared/backup session:
 * users/{uid}/workspaceState/lastSaved
 *
 * Saved archive/history:
 * users/{uid}/savedChats/{chatId}
 */
function getActiveWorkspaceRef(userId: string) {
  return doc(db, "users", userId, "workspaceState", "active");
}

function getLastSavedWorkspaceRef(userId: string) {
  return doc(db, "users", userId, "workspaceState", "lastSaved");
}

function getSavedChatsCollectionRef(userId: string) {
  return collection(db, "users", userId, "savedChats");
}

function getSavedChatRef(userId: string, chatId: string) {
  return doc(db, "users", userId, "savedChats", chatId);
}

function getActiveWorkspacePath(userId: string) {
  return `users/${userId}/workspaceState/active`;
}

function getLastSavedWorkspacePath(userId: string) {
  return `users/${userId}/workspaceState/lastSaved`;
}

function getSavedChatPath(userId: string, chatId: string) {
  return `users/${userId}/savedChats/${chatId}`;
}

function logFirebaseProjectCheck() {
  const actualProjectId = (firebaseConfig as any)?.projectId;

  if (actualProjectId !== EXPECTED_FIREBASE_PROJECT_ID) {
    console.error(
      `[Firebase Project Mismatch] Expected ${EXPECTED_FIREBASE_PROJECT_ID}, but app is using ${actualProjectId}.`,
    );
  } else {
    console.info(`[Firebase Project OK] Using ${actualProjectId}.`);
  }
}

logFirebaseProjectCheck();

function handleFirestoreError(
  error: unknown,
  operationType: OperationType,
  path: string | null,
) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo:
        auth.currentUser?.providerData?.map((provider) => ({
          providerId: provider.providerId,
          email: provider.email,
        })) || [],
    },
    operationType,
    path,
  };

  console.error("Firestore Error:", JSON.stringify(errInfo, null, 2));
  throw new Error(JSON.stringify(errInfo));
}

/**
 * Firestore does not allow undefined values.
 */
function cleanUndefined(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(cleanUndefined);
  }

  if (obj !== null && typeof obj === "object") {
    const proto = Object.getPrototypeOf(obj);

    if (proto !== null && proto !== Object.prototype) {
      return obj;
    }

    const cleanObj: any = {};

    for (const key of Object.keys(obj)) {
      const val = obj[key];

      if (val !== undefined) {
        cleanObj[key] = cleanUndefined(val);
      }
    }

    return cleanObj;
  }

  return obj;
}

function isOfflineOrUnavailable(err: any): boolean {
  const errMsg = (err?.message || String(err)).toLowerCase();

  return (
    errMsg.includes("offline") ||
    errMsg.includes("unavailable") ||
    errMsg.includes("client is offline") ||
    err?.code === "unavailable"
  );
}

function estimateBytes(value: unknown): number {
  try {
    return new Blob([JSON.stringify(value || {})]).size;
  } catch {
    return JSON.stringify(value || {}).length;
  }
}

function truncateStoredText(
  value: unknown,
  maxChars: number,
  label = "Payload optimized",
): string {
  const text = value === undefined || value === null ? "" : String(value);
  if (text.length <= maxChars) return text;
  return `${text.slice(0, maxChars)}\n\n[${label}: original content exceeded ${maxChars.toLocaleString()} characters and was trimmed for Firestore stability.]`;
}

function hasOptimizedPayloadMarker(value?: string): boolean {
  const text = String(value || "").toLowerCase();
  return (
    text.includes("[payload size optimized") ||
    text.includes("payload optimized") ||
    text.includes("binary data not stored") ||
    text.includes("cleared to optimize database storage")
  );
}

function isDataUrl(value?: string): boolean {
  return typeof value === "string" && /^data:[^;]+;base64,/i.test(value);
}

function isOversizedInlinePayload(value?: string): boolean {
  return Boolean(value && value.length > MAX_STORED_INLINE_PAYLOAD_CHARS);
}

/**
 * Firestore storage rule:
 * Never create a fake data:image/... placeholder. If a visual payload is too large for
 * Firestore, omit the binary field and leave explicit metadata saying the visual was
 * not stored in Firestore. App.tsx/InputEngine must supply current-turn images again
 * when visual inspection is needed.
 */
function stripLargeDataUrl(value?: string): string | undefined {
  if (!value) return value;
  if (hasOptimizedPayloadMarker(value)) return undefined;
  if (value.length <= MAX_STORED_INLINE_PAYLOAD_CHARS) return value;
  return undefined;
}

function storagePayloadState(
  value?: string,
): "stored" | "omitted_from_firestore" | "not_visual_payload" {
  if (!value) return "not_visual_payload";
  if (hasOptimizedPayloadMarker(value) || isOversizedInlinePayload(value))
    return "omitted_from_firestore";
  if (isDataUrl(value)) return "stored";
  return "not_visual_payload";
}

function getMimeTypeFromDataUrl(dataUrl?: string): string {
  const match = String(dataUrl || "").match(/^data:([^;]+);base64,/i);
  return match?.[1] || "image/png";
}

function extensionFromMimeType(mimeType?: string): string {
  const mime = String(mimeType || "").toLowerCase();
  if (mime.includes("jpeg") || mime.includes("jpg")) return "jpg";
  if (mime.includes("webp")) return "webp";
  if (mime.includes("gif")) return "gif";
  if (mime.includes("svg")) return "svg";
  return "png";
}

function stableHash(input: string): string {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = (Math.imul(31, hash) + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

function getGeneratedImageAssetId(
  messageId: string,
  imageUrl?: string,
  seed = "image",
): string {
  const hashSeed = imageUrl
    ? `${messageId}:${imageUrl.length}:${imageUrl.slice(0, 80)}`
    : `${messageId}:${seed}`;
  return `generated-${messageId || "message"}-${stableHash(hashSeed)}`
    .replace(/[^a-zA-Z0-9-_]/g, "-")
    .slice(0, 120);
}

function getGeneratedImageStoragePath(assetId: string): string {
  return `${GENERATED_IMAGE_LOCAL_STORAGE_PREFIX}${assetId}`;
}

function canUseBrowserLocalStorage(): boolean {
  try {
    return typeof window !== "undefined" && Boolean(window.localStorage);
  } catch {
    return false;
  }
}

function saveGeneratedImageDataUrlToLocalCache(
  assetId: string,
  dataUrl?: string,
): boolean {
  if (
    !assetId ||
    !dataUrl ||
    !isDataUrl(dataUrl) ||
    !canUseBrowserLocalStorage()
  )
    return false;

  try {
    window.localStorage.setItem(getGeneratedImageStoragePath(assetId), dataUrl);
    return true;
  } catch (err) {
    console.warn(
      "[Generated Image Cache] Could not persist image data locally.",
      err,
    );
    return false;
  }
}

function loadGeneratedImageDataUrlFromLocalCache(
  assetId?: string,
  storagePath?: string,
): string | undefined {
  if (!canUseBrowserLocalStorage()) return undefined;

  const key = storagePath?.startsWith(GENERATED_IMAGE_LOCAL_STORAGE_PREFIX)
    ? storagePath
    : assetId
      ? getGeneratedImageStoragePath(assetId)
      : "";

  if (!key) return undefined;

  try {
    const value = window.localStorage.getItem(key) || undefined;
    return value && isDataUrl(value) && !hasOptimizedPayloadMarker(value)
      ? value
      : undefined;
  } catch {
    return undefined;
  }
}

function makeGeneratedImageAssetFromImageUrl(
  message: ChatMessage,
): GeneratedImageAsset | undefined {
  const imageUrl = String(message.imageUrl || "");
  if (!imageUrl || hasOptimizedPayloadMarker(imageUrl)) return undefined;

  const isInline = isDataUrl(imageUrl);
  const mimeType = isInline ? getMimeTypeFromDataUrl(imageUrl) : "image/png";
  const id = getGeneratedImageAssetId(message.id, imageUrl, "imageUrl");

  return cleanUndefined({
    id,
    name: `generated-image-${id}.${extensionFromMimeType(mimeType)}`,
    mimeType,
    dataUrl: isInline ? imageUrl : undefined,
    url: isInline ? undefined : imageUrl,
    storagePath: isInline ? getGeneratedImageStoragePath(id) : undefined,
    source: "generated_image",
    createdAt: message.timestamp || new Date().toISOString(),
  }) as GeneratedImageAsset;
}

function normalizeGeneratedImageAssetForStorage(
  asset: GeneratedImageAsset | undefined,
): GeneratedImageAsset | undefined {
  if (!asset) return undefined;

  const id = String(asset.id || "").trim();
  if (!id) return undefined;

  const dataUrl = String(asset.dataUrl || "");
  const shouldKeepInline =
    dataUrl && dataUrl.length <= MAX_STORED_IMAGE_ASSET_DATA_URL_CHARS;
  const cached =
    dataUrl && isDataUrl(dataUrl)
      ? saveGeneratedImageDataUrlToLocalCache(id, dataUrl)
      : false;

  return cleanUndefined({
    ...asset,
    dataUrl: shouldKeepInline ? dataUrl : undefined,
    storagePath: cached ? getGeneratedImageStoragePath(id) : asset.storagePath,
  }) as GeneratedImageAsset;
}

function restoreGeneratedImageAsset(
  asset: GeneratedImageAsset | undefined,
): GeneratedImageAsset | undefined {
  if (!asset) return undefined;

  const cachedDataUrl = !asset.dataUrl
    ? loadGeneratedImageDataUrlFromLocalCache(asset.id, asset.storagePath)
    : undefined;

  return cleanUndefined({
    ...asset,
    dataUrl: asset.dataUrl || cachedDataUrl,
  }) as GeneratedImageAsset;
}

function collectMessageImageAssetsForStorage(
  message: ChatMessage,
): GeneratedImageAsset[] {
  const assets: GeneratedImageAsset[] = [];
  const seen = new Set<string>();

  const add = (asset?: GeneratedImageAsset) => {
    const normalized = normalizeGeneratedImageAssetForStorage(asset);
    if (!normalized?.id || seen.has(normalized.id)) return;
    seen.add(normalized.id);
    assets.push(normalized);
  };

  add(message.imageAsset);
  (message.imageAssets || []).forEach(add);
  add(makeGeneratedImageAssetFromImageUrl(message));

  return assets;
}

function restoreMessageImageUrl(
  message: ChatMessage,
  imageAssets: GeneratedImageAsset[],
): string | undefined {
  const direct = String(message.imageUrl || "");
  if (direct && !hasOptimizedPayloadMarker(direct)) return direct;

  const firstRenderableAsset = imageAssets.find(
    (asset) => asset.dataUrl || asset.url,
  );
  return firstRenderableAsset?.dataUrl || firstRenderableAsset?.url;
}

function safeChatId(seed?: string): string {
  const base = (seed || `chat-${Date.now()}`)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72);

  return `${base || "chat"}-${Date.now()}`;
}

function deriveChatTitle(
  messages: ChatMessage[],
  fallback = "RodgersPC Chat",
): string {
  const firstUseful = (messages || []).find((m) => {
    if (m.role !== "user") return false;
    const text = (m.content || "").trim();
    return text.length > 0 && !text.toLowerCase().includes("request failed");
  });

  const raw = (firstUseful?.content || fallback)
    .replace(/\s+/g, " ")
    .replace(/[`*_#>]/g, "")
    .trim();

  if (!raw) return fallback;

  return raw.length > 72 ? `${raw.slice(0, 72).trim()}…` : raw;
}

function sanitizeMessagesForStorage(
  messages: ChatMessage[],
  maxBytes = MAX_ACTIVE_WORKSPACE_BYTES,
): ChatMessage[] {
  const cleaned = (messages || []).map((m) => {
    const imageAssets = collectMessageImageAssetsForStorage(m);
    const primaryImageAsset = imageAssets[0];
    const imageUrlState = storagePayloadState(m.imageUrl);
    const storedImageUrl =
      m.imageUrl && imageUrlState === "stored" ? m.imageUrl : undefined;

    return cleanUndefined({
      ...m,
      content: truncateStoredText(
        m.content || "",
        MAX_STORED_MESSAGE_CHARS,
        "Message content optimized",
      ),
      imageUrl: storedImageUrl,
      imageAsset: primaryImageAsset,
      imageAssets,
      imagePayloadStorageState: imageUrlState,
      imagePayloadStoredInFirestore: imageUrlState === "stored",
      originalContent: m.originalContent
        ? truncateStoredText(
            m.originalContent,
            MAX_STORED_MESSAGE_CHARS,
            "Original message content optimized",
          )
        : m.originalContent,
      attachments: m.attachments?.map((att) => {
        const contentIsInlineBinary = isDataUrl(att.content);
        const base64IsInlineBinary = isDataUrl(att.base64);
        const visualSource = att.base64 || att.content || "";
        const payloadState = storagePayloadState(visualSource);
        const storedImageAsset = normalizeGeneratedImageAssetForStorage(
          att.imageAsset,
        );

        return cleanUndefined({
          ...att,
          content: contentIsInlineBinary
            ? stripLargeDataUrl(att.content)
            : truncateStoredText(
                att.content || "",
                MAX_STORED_ATTACHMENT_TEXT_CHARS,
                "Attachment text optimized",
              ),
          base64: base64IsInlineBinary
            ? stripLargeDataUrl(att.base64)
            : undefined,
          imageAsset: storedImageAsset,
          payloadStorageState: payloadState,
          visualPayloadStoredInFirestore: payloadState === "stored",
        });
      }),
      reports: m.reports?.map((rep) => ({
        ...rep,
        text: truncateStoredText(
          rep.text || "",
          MAX_STORED_REPORT_CHARS,
          "Report content optimized",
        ),
        originalText: rep.originalText
          ? truncateStoredText(
              rep.originalText,
              MAX_STORED_REPORT_CHARS,
              "Original report content optimized",
            )
          : rep.originalText,
      })),
    });
  });

  // If the workspace is still too large, keep the most recent thread. This protects Firestore writes from failing.
  if (estimateBytes(cleaned) <= maxBytes) return cleaned;

  const recent = cleaned.slice(-40);
  if (estimateBytes(recent) <= maxBytes) return recent;

  return recent.map((m) => ({
    ...m,
    content: truncateStoredText(
      m.content || "",
      40000,
      "Message content further optimized",
    ),
    attachments: m.attachments?.map((att) => ({
      ...att,
      content: truncateStoredText(
        att.content || "",
        2000,
        "Attachment payload further optimized",
      ),
      base64: undefined,
    })),
    imageUrl: undefined,
    reports: m.reports?.map((rep) => ({
      ...rep,
      text: truncateStoredText(
        rep.text || "",
        40000,
        "Report content further optimized",
      ),
      originalText: undefined,
    })),
  }));
}

function sanitizeMessagesAfterLoad(
  messages: ChatMessage[] | undefined,
): ChatMessage[] {
  return (messages || []).map((m) => {
    const restoredDirectAsset = restoreGeneratedImageAsset(
      (m as any).imageAsset,
    );
    const restoredAssetList = ((m as any).imageAssets || [])
      .map((asset: GeneratedImageAsset) => restoreGeneratedImageAsset(asset))
      .filter(Boolean) as GeneratedImageAsset[];

    const restoredAssets = (() => {
      const seen = new Set<string>();
      const assets: GeneratedImageAsset[] = [];
      for (const asset of [restoredDirectAsset, ...restoredAssetList]) {
        if (!asset?.id || seen.has(asset.id)) continue;
        seen.add(asset.id);
        assets.push(asset);
      }
      return assets;
    })();

    const restoredImageUrl = restoreMessageImageUrl(m, restoredAssets);

    return cleanUndefined({
      ...m,
      imageUrl: restoredImageUrl,
      imageAsset: restoredAssets[0],
      imageAssets: restoredAssets,
      imagePayloadStorageState:
        (m as any).imagePayloadStorageState ||
        storagePayloadState(restoredImageUrl),
      imagePayloadStoredInFirestore: Boolean(
        m.imageUrl &&
        isDataUrl(m.imageUrl) &&
        !hasOptimizedPayloadMarker(m.imageUrl),
      ),
      attachments: m.attachments?.map((att) => {
        const restoredAttachmentAsset = restoreGeneratedImageAsset(
          (att as any).imageAsset,
        );
        const assetPayload =
          restoredAttachmentAsset?.dataUrl || restoredAttachmentAsset?.url;
        const cleanedBase64 = hasOptimizedPayloadMarker(att.base64)
          ? undefined
          : att.base64;
        const cleanedContent = hasOptimizedPayloadMarker(att.content)
          ? undefined
          : att.content;
        const restoredPayload =
          cleanedBase64 || cleanedContent || assetPayload || "";
        const payloadState =
          (att as any).payloadStorageState ||
          storagePayloadState(restoredPayload);

        return cleanUndefined({
          ...att,
          base64:
            cleanedBase64 ||
            (assetPayload && isDataUrl(assetPayload)
              ? assetPayload
              : undefined),
          content: cleanedContent || assetPayload,
          imageAsset: restoredAttachmentAsset,
          payloadStorageState: payloadState,
          visualPayloadStoredInFirestore: Boolean(
            restoredPayload &&
            isDataUrl(restoredPayload) &&
            !hasOptimizedPayloadMarker(restoredPayload),
          ),
        });
      }),
    });
  }) as ChatMessage[];
}

function resetRuntimeIndexingStatus(
  status: WorkspaceItem["indexingStatus"],
): WorkspaceItem["indexingStatus"] {
  // Never restore "loading" from Firestore. Loading is runtime-only state.
  return status === "loading" ? "idle" : status;
}

function sanitizeDriveItemsForStorage(items: WorkspaceItem[]): WorkspaceItem[] {
  return (items || []).map((item) => ({
    id: item.id,
    name: item.name,
    type: item.type,
    path: item.path,
    parentId: item.parentId,
    mimeType: item.mimeType,
    modifiedTime: item.modifiedTime,
    webViewLink: item.webViewLink,
    iconLink: item.iconLink,
    shortcutDetails: item.shortcutDetails,
    size: item.size,

    // Drive focus contract:
    // checked/gold = focused RodgersPC Drive knowledge
    // unchecked = ignored
    checked: item.checked === true,
    sourceOfTruth: item.sourceOfTruth === true,

    // Never persist downloaded Drive contents, extracted images, or inline file payloads.
    // Content is reloaded on demand from Drive so Firestore stays stable and current.
    content: "",
    loadedContent: undefined,
    extractedImages: undefined,
    sourceType: item.sourceType,
    extractionStatus:
      item.extractionStatus === "loading" ? "idle" : item.extractionStatus,
    extractionWarnings: item.extractionWarnings,
    indexingStatus: resetRuntimeIndexingStatus(item.indexingStatus),

    hidden: item.hidden,
    error: item.error,
    expanded: item.expanded,
    depth: item.depth,
    indeterminate: false,

    children: item.children
      ? sanitizeDriveItemsForStorage(item.children)
      : undefined,
  }));
}

/**
 * Stored Drive tree normalizer.
 *
 * Current Drive model:
 * - checked/gold = focused Drive data
 * - unchecked = ignored
 *
 * Runtime-only content/loading state is stripped on restore.
 */
function normalizeStoredDriveFocus(items: WorkspaceItem[]): WorkspaceItem[] {
  return (items || []).map((item) => ({
    ...item,
    checked: item.checked === true,
    sourceOfTruth: item.sourceOfTruth === true,
    content: "",
    loadedContent: undefined,
    extractedImages: undefined,
    indexingStatus: resetRuntimeIndexingStatus(item.indexingStatus),
    extractionStatus:
      item.extractionStatus === "loading" ? "idle" : item.extractionStatus,
    indeterminate: false,
    children: item.children
      ? normalizeStoredDriveFocus(item.children)
      : undefined,
  }));
}

function getMessageCount(
  state: WorkspaceStatePayload | SavedChatRecord | null | undefined,
): number {
  if (!state || !Array.isArray(state.messages)) return 0;
  return state.messages.length;
}

function createActiveChatId(userId: string): string {
  return `${userId}-active`;
}

function normalizeModeForStorage(mode: any): AppMode {
  return Object.values(AppMode).includes(mode) ? mode : AppMode.TECH_ASSISTANT;
}

function normalizeBool(value: unknown, defaultValue: boolean): boolean {
  if (value === true) return true;
  if (value === false) return false;
  return defaultValue;
}

function normalizeAccuracyMode(value: unknown): AccuracyMode {
  return value === "balanced" || value === "creative" || value === "strict"
    ? value
    : "strict";
}

function normalizeResponseLength(value: unknown): ResponseLength {
  if (
    value === "tight" ||
    value === "short" ||
    value === "normal" ||
    value === "long" ||
    value === "full"
  ) {
    return value as ResponseLength;
  }

  // Legacy Phase 6/11 values kept readable but normalized into the Phase 13 slider scale.
  if (value === "quick") return "short" as ResponseLength;
  if (value === "detailed") return "long" as ResponseLength;

  return "short" as ResponseLength;
}

function normalizeJobSensitivity(value: unknown): JobSensitivity {
  return value === "routine" || value === "critical" || value === "important"
    ? value
    : "important";
}

function normalizeAiPreset(value: unknown): AiPreset {
  return value === "front_counter" ||
    value === "creative_seo" ||
    value === "custom" ||
    value === "bench_job"
    ? value
    : "bench_job";
}

function normalizeAiTemperature(
  value: unknown,
  accuracyMode: AccuracyMode,
  jobSensitivity: JobSensitivity,
): number {
  const raw = Number(value);
  if (Number.isFinite(raw)) return Math.max(0, Math.min(raw, 1));

  if (accuracyMode === "creative")
    return jobSensitivity === "critical" ? 0.5 : 0.55;
  if (accuracyMode === "balanced")
    return jobSensitivity === "critical" ? 0.2 : 0.25;
  return jobSensitivity === "critical" ? 0.1 : 0.02;
}

function normalizeStateForWrite(
  userId: string,
  state: WorkspaceStatePayload,
  maxMessageBytes = MAX_ACTIVE_WORKSPACE_BYTES,
): WorkspaceStatePayload {
  const normalizedAccuracyMode = normalizeAccuracyMode(
    state.accuracyMode || state.aiBehavior,
  );
  const normalizedResponseLength = normalizeResponseLength(
    state.responseLength,
  );
  const normalizedJobSensitivity = normalizeJobSensitivity(
    state.jobSensitivity,
  );
  const normalizedAiPreset = normalizeAiPreset(state.aiPreset);

  return cleanUndefined({
    ...state,
    userId,
    email: state.email || auth.currentUser?.email || "",
    currentMode: normalizeModeForStorage(state.currentMode),
    globalTheme: state.globalTheme || "rodgerspc",
    chatTheme: state.chatTheme || "dark",

    // Safe routing/control defaults:
    // - Drive ON by default because Source of Truth / Drive Brain is part of the app.
    // - Web OFF by default because live price/link claims must be intentional.
    // - Fast Mode OFF by default so server-side RodgersPC model routing can choose the correct model.
    driveSyncEnabled: normalizeBool(state.driveSyncEnabled, true),
    searchGroundingEnabled: normalizeBool(state.searchGroundingEnabled, false),
    imageGenerationEnabled: normalizeBool(state.imageGenerationEnabled, false),
    fastModeEnabled: normalizeBool(state.fastModeEnabled, false),

    driveSidebarVisible: normalizeBool(state.driveSidebarVisible, true),
    bottomPanelVisible: normalizeBool(state.bottomPanelVisible, true),

    aiTemperature: normalizeAiTemperature(
      state.aiTemperature,
      normalizedAccuracyMode,
      normalizedJobSensitivity,
    ),
    aiBehavior: normalizedAccuracyMode,
    aiVerbosity: state.aiVerbosity || "minimum",
    accuracyMode: normalizedAccuracyMode,
    responseLength: normalizedResponseLength,
    jobSensitivity: normalizedJobSensitivity,
    aiPreset: normalizedAiPreset,

    // Model selection is server-side automatic. Persist only the mode marker, never per-prompt hints.
    modelRoutingMode: DEFAULT_MODEL_ROUTING_MODE,
    modelRoutingVersion: WORKSPACE_ROUTING_VERSION,

    activeChatId: state.activeChatId || createActiveChatId(userId),
    messages: Array.isArray(state.messages)
      ? sanitizeMessagesForStorage(state.messages, maxMessageBytes)
      : [],
    items: Array.isArray(state.items)
      ? sanitizeDriveItemsForStorage(state.items)
      : [],
    lastUpdated: new Date().toISOString(),
  }) as WorkspaceStatePayload;
}

async function backupExistingActiveState(
  userId: string,
  existingState: WorkspaceStatePayload,
  source: string,
): Promise<void> {
  const lastSavedRef = getLastSavedWorkspaceRef(userId);
  const lastSavedPath = getLastSavedWorkspacePath(userId);

  const backupState = normalizeStateForWrite(
    userId,
    existingState,
    MAX_BACKUP_WORKSPACE_BYTES,
  );

  await setDoc(
    lastSavedRef,
    cleanUndefined({
      ...backupState,
      userId,
      email: backupState.email || auth.currentUser?.email || "",
      isCleared: false,
      backedUpAt: serverTimestamp(),
      backedUpAtIso: new Date().toISOString(),
      source,
    }),
    { merge: false },
  );

  console.info("[Firestore Backup Saved]", {
    path: lastSavedPath,
    backedUpMessages: getMessageCount(backupState),
    source,
  });
}

/**
 * Saves the active user workspace state.
 *
 * Protections:
 * - user-scoped path: users/{uid}/workspaceState/active
 * - startup blank/default state cannot overwrite a real active chat
 * - real Clear Chat only when state.isCleared === true
 * - previous chat is backed up before clear
 * - giant image/PDF/Drive payloads are not stored in Firestore
 */
export async function saveWorkspaceState(
  userId: string,
  state: WorkspaceStatePayload,
): Promise<void> {
  const activePath = getActiveWorkspacePath(userId);

  try {
    if (!userId) {
      console.warn("[Firestore Save Blocked] Missing userId.");
      return;
    }

    const activeRef = getActiveWorkspaceRef(userId);
    const incomingState = normalizeStateForWrite(
      userId,
      state,
      MAX_ACTIVE_WORKSPACE_BYTES,
    );

    const incomingMessageCount = getMessageCount(incomingState);
    const incomingIsCleared = incomingState.isCleared === true;

    const existingSnap = await getDoc(activeRef);
    const existingState = existingSnap.exists()
      ? (existingSnap.data() as WorkspaceStatePayload)
      : null;

    const existingMessageCount = getMessageCount(existingState);
    const existingIsCleared = existingState?.isCleared === true;

    console.info("[Firestore Save Debug]", {
      path: activePath,
      uid: userId,
      email: auth.currentUser?.email || incomingState.email,
      incomingMessages: incomingMessageCount,
      incomingIsCleared,
      incomingBytes: estimateBytes(incomingState),
      existingFound: existingSnap.exists(),
      existingMessages: existingMessageCount,
      existingIsCleared,
      currentMode: incomingState.currentMode,
      projectId: (firebaseConfig as any)?.projectId,
    });

    /**
     * Startup protection:
     * React/App state can briefly be blank/default while restoring.
     * Do not allow blank state to overwrite a real active chat.
     */
    if (
      !incomingIsCleared &&
      incomingMessageCount === 0 &&
      existingState &&
      !existingIsCleared &&
      existingMessageCount > 0
    ) {
      console.warn(
        "[Firestore Save Blocked] Prevented blank startup state from overwriting active chat.",
        {
          path: activePath,
          existingMessages: existingMessageCount,
        },
      );
      return;
    }

    /**
     * Clear Chat behavior:
     * Preserve the previous active chat first.
     */
    if (incomingIsCleared && existingState && existingMessageCount > 0) {
      await backupExistingActiveState(
        userId,
        existingState,
        "clear-chat-backup",
      );
    }

    await setDoc(
      activeRef,
      {
        ...incomingState,
        updatedAt: serverTimestamp(),
      },
      { merge: false },
    );

    console.info("[Firestore Save OK]", {
      path: activePath,
      savedMessages: incomingMessageCount,
      estimatedBytes: estimateBytes(incomingState),
      isCleared: incomingIsCleared,
    });
  } catch (err: any) {
    if (isOfflineOrUnavailable(err)) {
      console.warn(
        `Firestore upload skipped: client is offline/unavailable. Path: ${activePath}`,
      );
      return;
    }

    handleFirestoreError(err, OperationType.WRITE, activePath);
  }
}

function normalizeLoadedWorkspaceState(
  data: WorkspaceStatePayload,
): WorkspaceStatePayload {
  const normalizedAccuracyMode = normalizeAccuracyMode(
    data.accuracyMode || data.aiBehavior,
  );
  const normalizedJobSensitivity = normalizeJobSensitivity(data.jobSensitivity);

  return cleanUndefined({
    ...data,
    currentMode: normalizeModeForStorage(data.currentMode),
    driveSyncEnabled: normalizeBool(data.driveSyncEnabled, true),
    searchGroundingEnabled: normalizeBool(data.searchGroundingEnabled, false),
    imageGenerationEnabled: normalizeBool(data.imageGenerationEnabled, false),
    fastModeEnabled: normalizeBool(data.fastModeEnabled, false),
    aiTemperature: normalizeAiTemperature(
      data.aiTemperature,
      normalizedAccuracyMode,
      normalizedJobSensitivity,
    ),
    aiBehavior: normalizedAccuracyMode,
    accuracyMode: normalizedAccuracyMode,
    responseLength: normalizeResponseLength(data.responseLength),
    jobSensitivity: normalizedJobSensitivity,
    aiPreset: normalizeAiPreset(data.aiPreset),
    modelRoutingMode: DEFAULT_MODEL_ROUTING_MODE,
    modelRoutingVersion: WORKSPACE_ROUTING_VERSION,
    messages: sanitizeMessagesAfterLoad(data.messages),
    items: Array.isArray(data.items)
      ? normalizeStoredDriveFocus(data.items)
      : [],
  }) as WorkspaceStatePayload;
}

/**
 * Loads the active user workspace state.
 */
export async function loadWorkspaceState(
  userId: string,
): Promise<WorkspaceStatePayload | null> {
  const activePath = getActiveWorkspacePath(userId);

  try {
    if (!userId) {
      console.warn("[Firestore Load Blocked] Missing userId.");
      return null;
    }

    const activeRef = getActiveWorkspaceRef(userId);
    const snap = await getDoc(activeRef);

    if (!snap.exists()) {
      console.info("[Firestore Load] No active workspace state found.", {
        path: activePath,
        uid: userId,
        projectId: (firebaseConfig as any)?.projectId,
      });
      return null;
    }

    const data = snap.data() as WorkspaceStatePayload;

    console.info("[Firestore Load OK]", {
      path: activePath,
      uid: userId,
      email: auth.currentUser?.email || data.email,
      messages: getMessageCount(data),
      isCleared: data.isCleared === true,
      currentMode: data.currentMode,
      lastUpdated: data.lastUpdated,
      projectId: (firebaseConfig as any)?.projectId,
    });

    return normalizeLoadedWorkspaceState(data);
  } catch (err: any) {
    if (isOfflineOrUnavailable(err)) {
      console.warn(
        `Firestore load skipped: client is offline/unavailable. Path: ${activePath}`,
      );
      return null;
    }

    handleFirestoreError(err, OperationType.GET, activePath);
    return null;
  }
}

/**
 * Loads the backup made when Joshua intentionally clears a chat.
 */
export async function loadLastSavedWorkspaceState(
  userId: string,
): Promise<WorkspaceStatePayload | null> {
  const lastSavedPath = getLastSavedWorkspacePath(userId);

  try {
    if (!userId) {
      console.warn("[Firestore Last Saved Load Blocked] Missing userId.");
      return null;
    }

    const lastSavedRef = getLastSavedWorkspaceRef(userId);
    const snap = await getDoc(lastSavedRef);

    if (!snap.exists()) {
      console.info("[Firestore Last Saved Load] No backup found.", {
        path: lastSavedPath,
        uid: userId,
      });
      return null;
    }

    const data = snap.data() as WorkspaceStatePayload;

    console.info("[Firestore Last Saved Load OK]", {
      path: lastSavedPath,
      uid: userId,
      messages: getMessageCount(data),
      currentMode: data.currentMode,
    });

    return normalizeLoadedWorkspaceState(data);
  } catch (err: any) {
    if (isOfflineOrUnavailable(err)) {
      console.warn(
        `Firestore last-saved load skipped: client is offline/unavailable. Path: ${lastSavedPath}`,
      );
      return null;
    }

    handleFirestoreError(err, OperationType.GET, lastSavedPath);
    return null;
  }
}

/**
 * Clears the stored last-saved chat backup.
 */
export async function clearLastSavedWorkspaceState(
  userId: string,
): Promise<void> {
  const lastSavedPath = getLastSavedWorkspacePath(userId);

  try {
    if (!userId) {
      console.warn("[Firestore Last Saved Clear Blocked] Missing userId.");
      return;
    }

    await deleteDoc(getLastSavedWorkspaceRef(userId));

    console.info("[Firestore Last Saved Clear OK]", {
      path: lastSavedPath,
      uid: userId,
    });
  } catch (err: any) {
    if (isOfflineOrUnavailable(err)) {
      console.warn(
        `Firestore last-saved clear skipped: client is offline/unavailable. Path: ${lastSavedPath}`,
      );
      return;
    }

    handleFirestoreError(err, OperationType.DELETE, lastSavedPath);
  }
}

/**
 * Archives the current chat into a reusable chat-history collection.
 */
export async function saveChatArchive(
  userId: string,
  state: Partial<WorkspaceStatePayload>,
  title?: string,
): Promise<string | null> {
  const chatTitle = title?.trim() || deriveChatTitle(state.messages || []);
  const chatId = safeChatId(chatTitle);
  const path = getSavedChatPath(userId, chatId);

  try {
    if (!userId) {
      console.warn("[Firestore Chat Archive Blocked] Missing userId.");
      return null;
    }

    const nowIso = new Date().toISOString();
    const cleanedMessages = sanitizeMessagesForStorage(
      state.messages || [],
      MAX_ARCHIVE_BYTES,
    );
    const cleanedItems = sanitizeDriveItemsForStorage(state.items || []);

    const archivePayload: SavedChatRecord = cleanUndefined({
      id: chatId,
      userId,
      email: state.email || auth.currentUser?.email || "",
      title: chatTitle,
      mode: normalizeModeForStorage(state.currentMode),
      messageCount: cleanedMessages.length,
      estimatedBytes: estimateBytes({
        messages: cleanedMessages,
        items: cleanedItems,
      }),
      createdAtIso: nowIso,
      updatedAtIso: nowIso,
      archivedAtIso: nowIso,
      pinned: false,
      messages: cleanedMessages,
      items: cleanedItems,
      globalTheme: state.globalTheme,
      chatTheme: state.chatTheme,
      currentMode: normalizeModeForStorage(state.currentMode),
      driveSyncEnabled: state.driveSyncEnabled,
      searchGroundingEnabled: state.searchGroundingEnabled,
      imageGenerationEnabled: state.imageGenerationEnabled,
      fastModeEnabled: state.fastModeEnabled,
      accuracyMode: state.accuracyMode,
      responseLength: state.responseLength,
      jobSensitivity: state.jobSensitivity,
      aiPreset: normalizeAiPreset(state.aiPreset),
      modelRoutingMode: DEFAULT_MODEL_ROUTING_MODE,
      modelRoutingVersion: WORKSPACE_ROUTING_VERSION,
    }) as SavedChatRecord;

    await setDoc(
      getSavedChatRef(userId, chatId),
      {
        ...archivePayload,
        archivedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      },
      { merge: false },
    );

    console.info("[Firestore Chat Archive Saved]", {
      path,
      title: chatTitle,
      messages: cleanedMessages.length,
      estimatedBytes: archivePayload.estimatedBytes,
    });

    return chatId;
  } catch (err: any) {
    if (isOfflineOrUnavailable(err)) {
      console.warn(
        `Firestore chat archive skipped: client is offline/unavailable. Path: ${path}`,
      );
      return null;
    }

    handleFirestoreError(err, OperationType.WRITE, path);
    return null;
  }
}

/**
 * Lists saved chats for a future Chat History / Job Memory panel.
 */
export async function listChatArchives(
  userId: string,
  maxResults = 50,
): Promise<SavedChatSummary[]> {
  const path = `users/${userId}/savedChats`;

  try {
    if (!userId) {
      console.warn("[Firestore Chat Archive List Blocked] Missing userId.");
      return [];
    }

    const q = query(
      getSavedChatsCollectionRef(userId),
      orderBy("updatedAt", "desc"),
      limit(Math.max(1, Math.min(maxResults, 100))),
    );

    const snap = await getDocs(q);
    return snap.docs.map((entry) => {
      const data = entry.data() as SavedChatRecord;
      return {
        id: data.id || entry.id,
        title: data.title || "RodgersPC Chat",
        mode: data.mode || data.currentMode || AppMode.TECH_ASSISTANT,
        messageCount: data.messageCount || getMessageCount(data),
        estimatedBytes: data.estimatedBytes || estimateBytes(data),
        createdAtIso: data.createdAtIso || data.archivedAtIso || "",
        updatedAtIso: data.updatedAtIso || data.archivedAtIso || "",
        archivedAtIso: data.archivedAtIso,
        pinned: data.pinned === true,
      };
    });
  } catch (err: any) {
    if (isOfflineOrUnavailable(err)) {
      console.warn(
        `Firestore chat archive list skipped: client is offline/unavailable. Path: ${path}`,
      );
      return [];
    }

    handleFirestoreError(err, OperationType.LIST, path);
    return [];
  }
}

/**
 * Loads one archived chat by id.
 */
export async function loadChatArchive(
  userId: string,
  chatId: string,
): Promise<SavedChatRecord | null> {
  const path = getSavedChatPath(userId, chatId);

  try {
    if (!userId || !chatId) {
      console.warn(
        "[Firestore Chat Archive Load Blocked] Missing userId or chatId.",
      );
      return null;
    }

    const snap = await getDoc(getSavedChatRef(userId, chatId));
    if (!snap.exists()) return null;

    const data = snap.data() as SavedChatRecord;
    return normalizeLoadedWorkspaceState(data);
  } catch (err: any) {
    if (isOfflineOrUnavailable(err)) {
      console.warn(
        `Firestore chat archive load skipped: client is offline/unavailable. Path: ${path}`,
      );
      return null;
    }

    handleFirestoreError(err, OperationType.GET, path);
    return null;
  }
}

/**
 * Deletes one archived chat.
 */
export async function deleteChatArchive(
  userId: string,
  chatId: string,
): Promise<void> {
  const path = getSavedChatPath(userId, chatId);

  try {
    if (!userId || !chatId) {
      console.warn(
        "[Firestore Chat Archive Delete Blocked] Missing userId or chatId.",
      );
      return;
    }

    await deleteDoc(getSavedChatRef(userId, chatId));
    console.info("[Firestore Chat Archive Delete OK]", { path, uid: userId });
  } catch (err: any) {
    if (isOfflineOrUnavailable(err)) {
      console.warn(
        `Firestore chat archive delete skipped: client is offline/unavailable. Path: ${path}`,
      );
      return;
    }

    handleFirestoreError(err, OperationType.DELETE, path);
  }
}
